import { useState, useEffect } from "react";
import { X, ShieldWarning, Nut, Drop, Fish, Egg, Leaf, CircleDashed, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const COMMON_ALLERGENS = [
  { id: "nuts", label: "Nuts", Icon: Nut },
  { id: "gluten", label: "Gluten", Icon: CircleDashed },
  { id: "dairy", label: "Dairy", Icon: Drop },
  { id: "shellfish", label: "Shellfish", Icon: Warning },
  { id: "eggs", label: "Eggs", Icon: Egg },
  { id: "soy", label: "Soy", Icon: Leaf },
  { id: "fish", label: "Fish", Icon: Fish },
  { id: "sesame", label: "Sesame", Icon: CircleDashed },
  { id: "wheat", label: "Wheat", Icon: Leaf },
];

const SETTINGS_KEY = "carte-lens-settings";

export interface UserSettings {
  allergens: string[];
  homeCurrency: string;
}

export function loadSettings(): UserSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (_e) {
    // ignore
  }
  return { allergens: [], homeCurrency: "USD" };
}

export function saveSettings(settings: UserSettings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

interface AllergySettingsProps {
  open: boolean;
  onClose: () => void;
  settings: UserSettings;
  onSave: (settings: UserSettings) => void;
}

const AllergySettings = ({ open, onClose, settings, onSave }: AllergySettingsProps) => {
  const [allergens, setAllergens] = useState<string[]>(settings.allergens);

  useEffect(() => {
    setAllergens(settings.allergens);
  }, [settings, open]);

  if (!open) return null;

  const toggleAllergen = (id: string) => {
    setAllergens((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    onSave({ allergens, homeCurrency: settings.homeCurrency });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-border px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <ShieldWarning size={20} weight="fill" className="text-primary" />
            <h2 className="text-lg font-bold text-foreground">Allergies</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
            <X size={20} weight="regular" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {/* Allergy Section */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">Allergy Profile</h3>
            <p className="text-xs text-muted-foreground mb-3">
              Select your allergies. Dishes containing these will be flagged.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {COMMON_ALLERGENS.map((a) => {
                const active = allergens.includes(a.id);
                const Icon = a.Icon;
                return (
                  <button
                    key={a.id}
                    onClick={() => toggleAllergen(a.id)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-center ${
                      active
                        ? "border-red-400 bg-red-50 text-red-700"
                        : "border-border bg-white text-foreground hover:border-muted-foreground/30"
                    }`}
                  >
                    <Icon size={22} weight={active ? "fill" : "regular"} />
                    <span className="text-xs font-medium">{a.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-border px-5 py-4 rounded-b-2xl">
          <Button onClick={handleSave} className="w-full">
            Save Allergies
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AllergySettings;
